"use client"

import type { ReactNode } from "react"
import React, { useId } from "react"
import * as LabelPrimitive from "@radix-ui/react-label"
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group"
import clsx from "clsx"

import { cn } from "@productlane/lib/src/cn"

// Black-and-white radio matching the Checkbox visual style — no
// brand-colored fill. The selected dot uses the foreground text color
// so it works on both themes without a hard-coded color.

export const RadioGroup = React.forwardRef<
	React.ElementRef<typeof RadioGroupPrimitive.Root>,
	React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => (
	<RadioGroupPrimitive.Root
		ref={ref}
		className={clsx("flex flex-col gap-3", className)}
		{...props}
	/>
))

export interface RadioGroupItemProps extends Omit<
	React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>,
	"children"
> {
	label?: ReactNode
	description?: ReactNode
	size?: "sm" | "md"
	labelClassName?: string
}

export const RadioGroupItem = React.forwardRef<
	React.ElementRef<typeof RadioGroupPrimitive.Item>,
	RadioGroupItemProps
>(
	(
		{ className, label, description, size = "md", labelClassName, ...props },
		ref,
	) => {
		const id = useId()
		const dotSize = size === "sm" ? "h-1.5 w-1.5" : "h-2 w-2"
		return (
			<div className={cn("flex items-start gap-3", className)}>
				<RadioGroupPrimitive.Item
					ref={ref}
					id={id}
					className={clsx(
						"flex flex-none items-center justify-center rounded-full border border-gray-300 bg-white hover:border-gray-400 focus:outline-none focus-visible:ring-4 dark:border-gray-600 dark:bg-gray-900 dark:hover:border-gray-500",
						size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4",
						"data-[state=checked]:border-gray-900 dark:data-[state=checked]:border-gray-100",
						props.disabled &&
							"pointer-events-none bg-gray-200 dark:bg-gray-600",
					)}
					{...props}
				>
					<RadioGroupPrimitive.Indicator
						className={clsx(
							"rounded-full bg-gray-900 dark:bg-gray-100",
							dotSize,
						)}
					/>
				</RadioGroupPrimitive.Item>
				{(label || description) && (
					<LabelPrimitive.Root
						htmlFor={id}
						className={clsx(
							"block cursor-pointer text-left text-sm font-medium",
							labelClassName,
						)}
					>
						{label}
						{description && (
							<div className="mt-0.5 font-normal text-secondary">
								{description}
							</div>
						)}
					</LabelPrimitive.Root>
				)}
			</div>
		)
	},
)
